import { useContext, type ReactNode } from "react";

import { OrganizationContext } from "@/contexts/frontend/organization/OrganizationContext";

import { DirectDocumentBudgetContext } from "./DirectDocumentBudgetContext";
import { useDirectDocumentBudget } from "./useDirectDocumentBudget";

export interface DirectDocumentBudgetProviderProps {
    children: ReactNode;
    /** Undefined until the conversation exists. */
    conversationId?: number | string;
    /** Counted document ids staged in the composer but not yet persisted. */
    stagedIds?: readonly number[];
    /** Gate the fetch on a picker being open. */
    enabled?: boolean;
}

/**
 * Thin wiring: resolves the org-tunable cap and hands the hook down.
 *
 * The cap comes from the organization context and is never defaulted here. An
 * unresolved cap leaves the budget `pending`, which renders as the disabled
 * state; substituting a literal would disagree with the server the first time
 * the cap is retuned.
 *
 * Reads the context directly rather than through `useOrganization`, which
 * throws when no provider is mounted. An absent organization context is a
 * legitimate state for this provider and must resolve to `pending` — the
 * budget is a secondary affordance and may not take the whole chat surface
 * down with it. Every consumer that needs the org to exist keeps using
 * `useOrganization`.
 */
export function DirectDocumentBudgetProvider({
    children,
    conversationId,
    stagedIds,
    enabled,
}: DirectDocumentBudgetProviderProps) {
    const organization = useContext(OrganizationContext);
    const budget = useDirectDocumentBudget({
        conversationId,
        limit: organization?.chatMaxDirectDocuments,
        stagedIds,
        enabled,
    });

    return (
        <DirectDocumentBudgetContext.Provider value={budget}>
            {children}
        </DirectDocumentBudgetContext.Provider>
    );
}
